import { useState, useEffect } from 'react';
import {
  Box, Typography, Container, Grid, Card, CardContent, CardMedia, Chip,
  Skeleton, ToggleButton, ToggleButtonGroup, Select, MenuItem, FormControl,
  InputLabel, Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowForward, Groups, WorkspacePremium, SportsEsports, AccessTime, LocationOn, AutoAwesome, LocalCafe } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import EventCard from '../../components/events/EventCard';
import OnboardingModal from '../../components/shared/OnboardingModal';
import ProfileNudgeBanner from '../../components/shared/ProfileNudgeBanner';
import { EmptyState } from '../../components/shared';
import eventsService, { EventItem } from '../../services/eventsService';
import { colors, designTokens } from '../../theme/theme';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { queryKeys } from '../../utils/queryKeys';

export default function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [zoneFilter, setZoneFilter] = useState<string>('');

  // The 'learn' tab requests no event_type filter (client-side narrows
  // to masterclass + knowledge_session below).
  const eventsFilters = {
    zone_id: zoneFilter || undefined,
    event_type: typeFilter === 'all' || typeFilter === 'learn' ? undefined : typeFilter,
  };
  const { data: events = [], isLoading: loading } = useQuery({
    queryKey: queryKeys.events.weekly(eventsFilters),
    queryFn: ({ signal }) => eventsService.getWeekly(eventsFilters, signal),
  });
  const { data: zones = [] } = useQuery({
    queryKey: queryKeys.events.zones(),
    queryFn: ({ signal }) => eventsService.getZones(signal),
  });

  // AI-powered recommendations are auth-gated; the `enabled` flag prevents
  // a 401 burst from anonymous visitors.
  const { data: recommended = [], isLoading: recLoading } = useQuery<EventItem[]>({
    queryKey: queryKeys.events.recommended(),
    queryFn: ({ signal }) => eventsService.getRecommended(signal),
    enabled: isAuthenticated,
  });

  // Show onboarding for new users (first time seeing home page after registration)
  useEffect(() => {
    if (isAuthenticated && !localStorage.getItem('cm_onboarding_seen')) {
      setShowOnboarding(true);
    }
  }, [isAuthenticated]);

  const greeting = isAuthenticated
    ? `Welcome back, ${user?.first_name || 'friend'}`
    : 'Find your table this week';

  return (
    <Box>
      {/* ── Hero Section — Warm, inviting, breathing ── */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 5, md: 8 },
          pb: { xs: 6, md: 10 },
          px: 2,
        }}
      >
        {/* Dark ambient gradient with warm brown glow */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `
              radial-gradient(ellipse 70% 60% at 15% 35%, rgba(180,100,60,0.22) 0%, transparent 65%),
              radial-gradient(ellipse 50% 50% at 5% 15%, rgba(140,80,40,0.18) 0%, transparent 55%),
              radial-gradient(ellipse 40% 40% at 80% 60%, rgba(138,92,246,0.1) 0%, transparent 60%),
              radial-gradient(ellipse 30% 30% at 50% 85%, rgba(212,88,106,0.06) 0%, transparent 50%),
              ${colors.background.default}
            `,
            zIndex: 0,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Overline */}
          <Typography
            variant="overline"
            sx={{
              color: colors.primary.main,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 1.5,
            }}
          >
            This week in Baku
          </Typography>

          {/* Main heading */}
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              color: '#FFFFFF',
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.2rem' },
              maxWidth: 640,
              lineHeight: 1.1,
            }}
          >
            {greeting}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body1"
            sx={{
              color: colors.neutral[500],
              mb: 4,
              maxWidth: 480,
              fontSize: { xs: '1rem', md: '1.15rem' },
              lineHeight: 1.6,
            }}
          >
            {isAuthenticated
              ? 'Explore this week\'s gatherings. Grab a coffee, meet someone new.'
              : 'Meet interesting people over great coffee. Pre-commit with a voucher, show up, and connect.'}
          </Typography>

          {/* CTA for non-authenticated users */}
          {!isAuthenticated && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/register')}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 14,
                  fontSize: '1rem',
                  background: colors.primary.gradient,
                  '&:hover': { boxShadow: designTokens.shadow.glow },
                }}
              >
                {t('auth.get_started')}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: 14,
                  borderColor: 'rgba(255,255,255,0.12)',
                  color: '#FFFFFF',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.25)', bgcolor: 'rgba(255,255,255,0.04)' },
                }}
              >
                I have an account
              </Button>
            </Box>
          )}

          {/* ── How it works — 3 quick steps ── */}
          {!isAuthenticated && (
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 2, md: 4 },
                mt: { xs: 5, md: 6 },
                flexWrap: 'wrap',
              }}
            >
              {[
                { icon: <WorkspacePremium sx={{ fontSize: 22 }} />, label: 'Browse topics', desc: 'Professional, social, or master class' },
                { icon: <Groups sx={{ fontSize: 22 }} />, label: 'Commit a seat', desc: 'Pre-pay your coffee' },
                { icon: <SportsEsports sx={{ fontSize: 22 }} />, label: 'Show up & connect', desc: '90 min, 5-6 people' },
              ].map((step) => (
                <Box
                  key={step.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    flex: { xs: '1 1 100%', sm: '1 1 auto' },
                  }}
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 12,
                      bgcolor: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: colors.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                      {step.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.neutral[400] }}>
                      {step.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Container>
      </Box>

      {/* ── Picked for You — AI Recommendations ── */}
      {isAuthenticated && (recLoading || recommended.length > 0) && (
        <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AutoAwesome sx={{ color: colors.accent.main, fontSize: 22 }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#FFFFFF',
                fontSize: { xs: '1.2rem', md: '1.4rem' },
              }}
            >
              Picked for You
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2.5,
              overflowX: 'auto',
              pb: 2,
              scrollSnapType: 'x mandatory',
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-track': { bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 3 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 3 },
            }}
          >
            {recLoading ? (
              // Shimmer loading state — 3 skeleton cards
              [1, 2, 3].map((n) => (
                <Box
                  key={`rec-skel-${n}`}
                  sx={{
                    minWidth: { xs: 280, sm: 300 },
                    maxWidth: 320,
                    flexShrink: 0,
                    scrollSnapAlign: 'start',
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    height={260}
                    sx={{ borderRadius: 5 }}
                    animation="wave"
                  />
                </Box>
              ))
            ) : (
              recommended.map((evt) => (
                <Card
                  key={`rec-${evt.id}`}
                  onClick={() => navigate(`/events/${evt.id}`)}
                  sx={{
                    minWidth: { xs: 280, sm: 300 },
                    maxWidth: 320,
                    flexShrink: 0,
                    scrollSnapAlign: 'start',
                    cursor: 'pointer',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '20px',
                    transition: `all ${designTokens.transition.normal}`,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `${designTokens.shadow.lg}, 0 0 20px rgba(232,135,92,0.1)`,
                      borderColor: 'rgba(232,135,92,0.2)',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      loading="lazy"
                      height="130"
                      image={evt.venue?.photo_url || 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=600'}
                      alt={evt.venue?.name || 'Venue'}
                      sx={{ objectFit: 'cover' }}
                    />
                    <Box sx={{ position: 'absolute', top: 10, left: 10 }}>
                      {/* DS Frosted Glass Badge — white label on frosted
                          glass; type accent lives on the card-body chip. */}
                      <Chip
                        label={evt.event_type === 'masterclass' ? 'Master Class' : evt.event_type === 'professional' ? 'Professional' : 'Social'}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.12)',
                          backdropFilter: 'blur(12px)',
                          color: '#FFFFFF',
                          fontWeight: 700,
                          fontSize: '0.68rem',
                          height: 24,
                          borderRadius: `${designTokens.radius.sm}px`,
                        }}
                      />
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 2, pb: '16px !important' }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: '#FFFFFF',
                        fontSize: '0.95rem',
                        lineHeight: 1.3,
                        mb: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {evt.title}
                    </Typography>
                    {evt.venue && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                        <LocationOn sx={{ fontSize: 14, color: colors.neutral[400] }} />
                        <Typography variant="caption" sx={{ color: colors.neutral[500] }}>
                          {evt.venue.name}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <AccessTime sx={{ fontSize: 14, color: colors.neutral[400] }} />
                      <Typography variant="caption" sx={{ color: colors.neutral[500] }}>
                        {evt.scheduled_date} {evt.scheduled_time ? `at ${evt.scheduled_time.slice(0, 5)}` : ''}
                      </Typography>
                    </Box>
                    {evt.match_reason && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontStyle: 'italic',
                          color: colors.primary.light,
                          lineHeight: 1.4,
                        }}
                      >
                        {evt.match_reason}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        </Container>
      )}

      {/* ── Filters — Clean, pill-shaped ── */}
      <Container maxWidth="lg" sx={{ mt: -1, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <ToggleButtonGroup
            value={typeFilter}
            exclusive
            onChange={(_, val) => { if (val !== null) setTypeFilter(val); }}
            size="small"
          >
            <ToggleButton value="all">{t('events.all')}</ToggleButton>
            <ToggleButton value="professional">{t('events.professional')}</ToggleButton>
            <ToggleButton value="social">{t('events.social')}</ToggleButton>
            <ToggleButton value="learn">{t('events.learn')}</ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>{t('events.filter_zone')}</InputLabel>
            <Select value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)} label="Zone">
              <MenuItem value="">All Zones</MenuItem>
              {zones.map(z => (
                <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Container>

      {/* ── Profile Nudge + Events Grid ── */}
      <Container maxWidth="lg" sx={{ pb: 8 }}>
        {isAuthenticated && <ProfileNudgeBanner />}
      </Container>

      <Container maxWidth="lg" sx={{ pb: 8 }}>
        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map(n => (
              <Grid item xs={12} sm={6} md={4} key={`skeleton-${n}`}>
                <Skeleton
                  variant="rounded"
                  height={380}
                  sx={{ borderRadius: 5 }}
                  animation="wave"
                />
              </Grid>
            ))}
          </Grid>
        ) : events.length === 0 ? (
          /* EmptyState + LocalCafe icon (icon, not emoji — stable
              cross-platform rendering). */
          <EmptyState
            icon={<LocalCafe />}
            title={t('events.no_events')}
            description="The next lineup drops Monday. Check back soon — great conversations are brewing."
          />
        ) : (
          <>
            {(() => {
              const displayEvents = typeFilter === 'learn'
                ? events.filter(e => e.event_type === 'masterclass' || e.event_type === 'knowledge_session')
                : events;
              return (
                <>
                  <Typography variant="body2" sx={{ color: colors.neutral[400], mb: 2.5 }}>
                    {displayEvents.length} event{displayEvents.length !== 1 ? 's' : ''} this week
                  </Typography>
                  <Grid container spacing={3}>
                    {displayEvents.map(event => (
                      <Grid item xs={12} sm={6} md={4} key={event.id}>
                        <EventCard event={event} />
                      </Grid>
                    ))}
                  </Grid>
                </>
              );
            })()}
          </>
        )}
      </Container>

      {/* Onboarding Modal for new users */}
      <OnboardingModal open={showOnboarding} onClose={() => setShowOnboarding(false)} />
    </Box>
  );
}
